import React, { useEffect, useMemo, useState } from 'react';
import styles from './MeditechSchedulingDemo.module.css';
import uscoreCollection from './uscorePostmanCollection.json';

type DemoStep = 1 | 2 | 3 | 4;

type DemoPatient = {
  id: string;
  name: string;
  dob: string;
  phone: string;
  email: string;
};

type DemoProvider = {
  id: string;
  name: string;
  specialty: string;
  location: string;
};

type DemoSlot = {
  id: string;
  providerId: string;
  date: string;
  time: string;
  type: 'In-Person' | 'Virtual';
};

type ApiStatus = 'live' | 'mock' | 'restricted';
type ApiCallStatus = 'idle' | 'loading' | 'success' | 'error';
type DataSource = 'live' | 'demo';

type PostmanItem = {
  name: string;
  item?: PostmanItem[];
  request?: {
    method?: string;
    url?: {
      raw?: string;
    };
  };
};

type UscoreRequest = {
  id: string;
  name: string;
  method: string;
  rawUrl: string;
};

type ApiCallResult = {
  status: ApiCallStatus;
  code?: number;
  durationMs?: number;
  snippet?: string;
  error?: string;
};

const DEFAULT_FHIR_BASE = 'https://greenfield-prod-apis.meditech.com/v2/uscore/R4';
const DEFAULT_PATIENT_ID = '0218f2d0-968b-5888-976f-68a554670f6e';

const demoPatients: DemoPatient[] = [
  {
    id: DEFAULT_PATIENT_ID,
    name: 'Sarai Mccall',
    dob: '1959-08-14',
    phone: '(555) 212-0198',
    email: 'sarai.mccall@example.com',
  },
  {
    id: '1a5c2b8a-1b6e-4f9b-9c07-2f0e813e6c2b',
    name: 'Luis Anderson',
    dob: '1972-11-02',
    phone: '(555) 555-1266',
    email: 'luis.anderson@example.com',
  },
];

const demoProviders: DemoProvider[] = [
  {
    id: 'p-103',
    name: 'Dr. Emily Carter',
    specialty: 'Family Medicine',
    location: 'Norwood Clinic',
  },
  {
    id: 'p-207',
    name: 'Dr. Jordan Lee',
    specialty: 'Cardiology',
    location: 'Norwood Clinic',
  },
  {
    id: 'p-311',
    name: 'Dr. Priya Raman',
    specialty: 'Endocrinology',
    location: 'Riverbend Medical',
  },
];

const demoSlots: DemoSlot[] = [
  { id: 's-1', providerId: 'p-103', date: '2026-02-11', time: '09:30 AM', type: 'In-Person' },
  { id: 's-2', providerId: 'p-103', date: '2026-02-11', time: '01:00 PM', type: 'Virtual' },
  { id: 's-3', providerId: 'p-207', date: '2026-02-12', time: '10:15 AM', type: 'In-Person' },
  { id: 's-4', providerId: 'p-311', date: '2026-02-13', time: '03:00 PM', type: 'Virtual' },
];

const getBundleEntries = (payload: any) => {
  if (!payload) return [];
  if (payload.resourceType === 'Bundle' && Array.isArray(payload.entry)) {
    return payload.entry.map((entry: any) => entry.resource).filter(Boolean);
  }
  if (Array.isArray(payload.entry)) return payload.entry;
  return [];
};

const apiCoverage: Array<{
  area: string;
  endpoints: string[];
  status: ApiStatus;
  note: string;
}> = [
  {
    area: 'FHIR US Core',
    endpoints: ['metadata', 'Patient', 'Location', 'Encounter', 'Observation'],
    status: 'live',
    note: 'Callable in sandbox using Postman token',
  },
  {
    area: 'FHIR Scheduling',
    endpoints: ['Appointment (argoScheduling)'],
    status: 'restricted',
    note: 'Requires Greenfield Scheduling access in Expanse',
  },
  {
    area: 'Pre-visit + Eligibility',
    endpoints: ['Coverage', 'Organization', 'Practitioner'],
    status: 'mock',
    note: 'Mocked to show workflow before access',
  },
  {
    area: 'Patient Search',
    endpoints: ['Patient?name', 'Patient?identifier'],
    status: 'live',
    note: 'Searchable via FHIR US Core',
  },
];

const workflowSteps: Array<{
  step: string;
  purpose: string;
  endpoints: string[];
  status: ApiStatus;
  note: string;
}> = [
  {
    step: '1. Patient identity + linking',
    purpose: 'Confirm patient identity and access scope for scheduling.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Patient/'],
    status: 'restricted',
    note: 'Patient-based access requires authenticated patient context.',
  },
  {
    step: '2. Location discovery',
    purpose: 'Show clinics and facility options for scheduling.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Location/'],
    status: 'restricted',
    note: 'Search by name/address to map clinic availability.',
  },
  {
    step: '3. Provider discovery',
    purpose: 'List practitioners available for scheduling.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Practitioner/'],
    status: 'restricted',
    note: 'Supports filtering by practitioner identifiers.',
  },
  {
    step: '4. Appointment search',
    purpose: 'Fetch available appointments by patient, location, or provider.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Appointment/?patient|practitioner|location'],
    status: 'restricted',
    note: 'Query filters include date, status, location, and practitioner.',
  },
  {
    step: '5. Hold a slot',
    purpose: 'Reserve a candidate appointment before booking.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Appointment/{id}/$hold'],
    status: 'restricted',
    note: 'Used to prevent double-booking while patient confirms.',
  },
  {
    step: '6. Book appointment',
    purpose: 'Commit the appointment for the patient.',
    endpoints: ['v2/argoScheduling/{implementationVersion}/Appointment/{id}/$book'],
    status: 'restricted',
    note: 'Captures patient + reason-for-visit for final booking.',
  },
  {
    step: '7. Track or manage appointment',
    purpose: 'Retrieve or update an appointment for confirmations or changes.',
    endpoints: [
      'v2/argoScheduling/{implementationVersion}/Appointment/{id}/',
      'v2/argoScheduling/{implementationVersion}/Appointment/?patient&status',
    ],
    status: 'restricted',
    note: 'Supports appointment status queries post-booking.',
  },
  {
    step: '8. US Core validation',
    purpose: 'Validate patient demographics and coverage in sandbox.',
    endpoints: ['v2/uscore/R4/Patient', 'v2/uscore/R4/Location'],
    status: 'live',
    note: 'Live in sandbox with Postman authorization_code token.',
  },
];

const faqItems: Array<{
  question: string;
  answer: string;
}> = [
  {
    question: 'Why use argoScheduling for booking instead of US Core Appointment?',
    answer:
      'US Core is for clinical interoperability. The scheduling workflow (search, hold, book) is defined in Argonaut Scheduling with scheduling scopes and operations like $hold/$book.',
  },
  {
    question: 'Which exact APIs will you use for patient scheduling?',
    answer:
      'We will use: Patient, Location, Practitioner, Appointment search, Appointment/{id}, Appointment/{id}/$hold, Appointment/{id}/$book under v2/argoScheduling/R4. US Core is used for ValueSet expansion and optional demographic validation.',
  },
  {
    question: 'How do you determine STU3 vs R4?',
    answer:
      'We default to R4 for modern FHIR alignment. If the tenant only exposes STU3, we can target that version because the endpoints are versioned by implementationVersion.',
  },
  {
    question: 'How does patient identity and access work?',
    answer:
      'Scheduling is patient-based. The user authenticates with an identity provider, then access is limited to the linked patient record. We only request the patient/ArgoScheduling scopes needed for scheduling.',
  },
  {
    question: 'How do you connect to an on-prem Meditech Expanse system?',
    answer:
      'We connect through Greenfield endpoints exposed by the client’s Meditech Expanse environment (tenant subdomain). We do not need direct database access; we use OAuth + FHIR APIs over HTTPS.',
  },
  {
    question: 'What Meditech system is this client on?',
    answer:
      'Client system: "MEDITECH Health Care Information System, Copyright © 2026 Medical Information Technology, Inc." Our plan assumes a Greenfield-enabled Expanse tenant.',
  },
  {
    question: 'What is required to connect to the client Expanse system at ehr.clienthealthcare.org?',
    answer:
      'Assumption: ehr.clienthealthcare.org is the client’s Expanse login domain. We connect through Greenfield OAuth and FHIR endpoints exposed by that tenant. Client enables Greenfield access, Meditech provisions scheduling APIs, and our app uses SMART-on-FHIR OAuth with the approved scopes.',
  },
  {
    question: 'How do you handle sandbox vs production gaps?',
    answer:
      'We demonstrate US Core in sandbox for patient lookup/validation and show the full scheduling flow with mocked data. Once scheduling access is enabled, we switch to live Argonaut Scheduling endpoints.',
  },
  {
    question: 'What is available today in this experience?',
    answer:
      'Today: US Core read endpoints (Patient, Location, Practitioner, Observation, etc.) are callable with a Postman token; the scheduling workflow is shown end-to-end with sample data. Pending: Argo Scheduling appointment operations ($hold/$book) require Greenfield Scheduling enablement on the client tenant.',
  },
  {
    question: 'What are the exact steps to connect to the client Expanse tenant?',
    answer:
      '1) Confirm tenant domain and Greenfield OAuth/FHIR endpoints. 2) Meditech enables Argo Scheduling APIs and scopes. 3) Client registers our SMART-on-FHIR app (client ID/secret, redirect URIs). 4) We implement authorization_code flow and map patient identity. 5) We call Appointment search, $hold, and $book for Annual Wellness scheduling. 6) UAT in sandbox and client tenant, then production launch.',
  },
  {
    question: 'How do you prevent double-booking?',
    answer:
      'We use the Appointment $hold operation during confirmation, then finalize with $book.',
  },
  {
    question: 'What data do you write back?',
    answer:
      'Only appointment booking actions via Argonaut Scheduling, including reason-for-visit and selected slot. We do not write clinical data.',
  },
];

const integrationSteps: Array<{
  step: string;
  owner: string;
  details: string[];
}> = [
  {
    step: '1. Confirm tenant and endpoints',
    owner: 'Client + Meditech',
    details: [
      'Confirm Expanse tenant domain (assumed: ehr.clienthealthcare.org).',
      'Confirm Greenfield FHIR base and OAuth endpoints for that tenant.',
      'Confirm implementationVersion (R4 preferred, STU3 fallback).',
    ],
  },
  {
    step: '2. Enable Greenfield scheduling access',
    owner: 'Meditech',
    details: [
      'Provision Argo Scheduling APIs for the tenant.',
      'Enable scopes for patient/ArgoScheduling.* and US Core read scopes as needed.',
      'Whitelist client application and redirect URIs.',
    ],
  },
  {
    step: '3. Client application registration',
    owner: 'Client + Meditech',
    details: [
      'Register our application in Greenfield Workspace.',
      'Issue client ID/secret and define OAuth grant (SMART-on-FHIR authorization_code).',
      'Set allowed redirect URIs and JWT/PKCE settings.',
    ],
  },
  {
    step: '4. Our platform setup',
    owner: 'Stitchfin',
    details: [
      'Configure OAuth flow against client tenant endpoints.',
      'Store tokens securely and rotate/refresh per policy.',
      'Map patient identity to Meditech Patient identifiers after login.',
    ],
  },
  {
    step: '5. Scheduling workflow enablement',
    owner: 'Stitchfin',
    details: [
      'Fetch locations and practitioners via argoScheduling or US Core.',
      'Search available appointments (Appointment?patient|practitioner|location).',
      'Hold slot with Appointment/{id}/$hold, then finalize with $book.',
    ],
  },
  {
    step: '6. Annual Wellness Visit scheduling',
    owner: 'Stitchfin + Client',
    details: [
      'Use Appointment type/value set mappings for AWV visit types.',
      'Collect required reason-for-visit and patient contact details.',
      'Confirm appointment status and provide reminders.',
    ],
  },
  {
    step: '7. Operational readiness',
    owner: 'Client + Stitchfin',
    details: [
      'Validate audit logging, error handling, and rate limits.',
      'Run UAT with sandbox data, then production pilot.',
      'Monitor booking success, cancellations, and reschedules.',
    ],
  },
];

const extractPostmanRequests = (items: PostmanItem[], parentName = ''): UscoreRequest[] => {
  const collected: UscoreRequest[] = [];
  items.forEach((item) => {
    const nextParentName = parentName ? `${parentName} / ${item.name}` : item.name;
    if (item.item && item.item.length > 0) {
      collected.push(...extractPostmanRequests(item.item, nextParentName));
      return;
    }
    const rawUrl = item.request?.url?.raw;
    if (!rawUrl) return;
    collected.push({
      id: `${nextParentName}::${rawUrl}`,
      name: nextParentName,
      method: item.request?.method || 'GET',
      rawUrl,
    });
  });
  return collected;
};

const MeditechSchedulingDemo: React.FC = () => {
  const [activeStep, setActiveStep] = useState<DemoStep>(1);
  const [selectedPatient, setSelectedPatient] = useState<DemoPatient | null>(null);
  const [appointmentType, setAppointmentType] = useState<'In-Person' | 'Virtual'>('In-Person');
  const [location, setLocation] = useState('Norwood Clinic');
  const [selectedProvider, setSelectedProvider] = useState<DemoProvider | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<DemoSlot | null>(null);
  const [notes, setNotes] = useState('');
  const [providersSource, setProvidersSource] = useState<DataSource>('demo');
  const [locationsSource, setLocationsSource] = useState<DataSource>('demo');
  const [patientsSource, setPatientsSource] = useState<DataSource>('demo');
  const [providersLoading, setProvidersLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [providersError, setProvidersError] = useState('');
  const [locationsError, setLocationsError] = useState('');
  const [patientsError, setPatientsError] = useState('');
  const [liveProviders, setLiveProviders] = useState<DemoProvider[]>([]);
  const [liveLocations, setLiveLocations] = useState<string[]>([]);
  const [livePatients, setLivePatients] = useState<DemoPatient[]>([]);

  const [searchName, setSearchName] = useState('Sarai Mccall');
  const [searchDob, setSearchDob] = useState('1959-08-14');

  const [accessToken, setAccessToken] = useState('');
  const [fhirBase, setFhirBase] = useState(DEFAULT_FHIR_BASE);
  const [fhirPatientId, setFhirPatientId] = useState(DEFAULT_PATIENT_ID);
  const [fhirStatus, setFhirStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [fhirEndpoint, setFhirEndpoint] = useState('metadata');
  const [fhirResponse, setFhirResponse] = useState('');
  const [fhirError, setFhirError] = useState('');
  const [uscoreFilter, setUscoreFilter] = useState('');
  const [uscoreVisibleCount, setUscoreVisibleCount] = useState(12);
  const [uscoreResults, setUscoreResults] = useState<Record<string, ApiCallResult>>({});
  const [uscoreRunning, setUscoreRunning] = useState(false);
  const [uscoreRunError, setUscoreRunError] = useState('');

  const availableProviders = useMemo(() => {
    const providers = providersSource === 'live' ? liveProviders : demoProviders;
    return providers.filter((provider) => provider.location === location);
  }, [location, liveProviders, providersSource]);

  const availableSlots = useMemo(() => {
    if (!selectedProvider) {
      return demoSlots.filter((slot) => slot.type === appointmentType);
    }
    return demoSlots.filter(
      (slot) => slot.providerId === selectedProvider.id && slot.type === appointmentType
    );
  }, [selectedProvider, appointmentType]);

  const uscoreRequests = useMemo(() => {
    const items = (uscoreCollection?.item || []) as PostmanItem[];
    return extractPostmanRequests(items);
  }, []);

  const filteredUscoreRequests = useMemo(() => {
    const query = uscoreFilter.trim().toLowerCase();
    if (!query) return uscoreRequests;
    return uscoreRequests.filter(
      (entry) =>
        entry.name.toLowerCase().includes(query) ||
        entry.rawUrl.toLowerCase().includes(query) ||
        entry.method.toLowerCase().includes(query)
    );
  }, [uscoreFilter, uscoreRequests]);

  const resolvedLocations = useMemo(() => {
    return locationsSource === 'live' ? liveLocations : ['Norwood Clinic', 'Riverbend Medical'];
  }, [locationsSource, liveLocations]);

  const resolvedPatients = useMemo(() => {
    return patientsSource === 'live' ? livePatients : demoPatients;
  }, [patientsSource, livePatients]);

  const resetBooking = () => {
    setActiveStep(1);
    setSelectedPatient(null);
    setSelectedProvider(null);
    setSelectedSlot(null);
    setNotes('');
  };

  const fetchUscore = async (path: string) => {
    const trimmedBase = fhirBase.replace(/\/+$/, '');
    const url = `${trimmedBase}/${path}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/fhir+json',
      },
    });
    const json = await response.json();
    return json;
  };

  useEffect(() => {
    if (!accessToken) {
      setProvidersSource('demo');
      setLocationsSource('demo');
      setPatientsSource('demo');
      setLiveProviders([]);
      setLiveLocations([]);
      setLivePatients([]);
      return;
    }

    const loadLocations = async () => {
      setLocationsLoading(true);
      setLocationsError('');
      try {
        const payload = await fetchUscore('Location?name=Norwood');
        const entries = getBundleEntries(payload);
        const names = entries
          .map((loc: any) => loc?.name)
          .filter((name: string) => typeof name === 'string' && name.trim().length > 0);
        const resolved = names.length > 0 ? names : ['Norwood Clinic'];
        setLiveLocations(resolved);
        setLocation(resolved[0]);
        setLocationsSource('live');
        return resolved[0];
      } catch (error) {
        setLocationsError('Unable to load locations from US Core.');
        setLocationsSource('demo');
        return 'Norwood Clinic';
      } finally {
        setLocationsLoading(false);
      }
    };

    const loadProviders = async (defaultLocation: string) => {
      setProvidersLoading(true);
      setProvidersError('');
      try {
        const payload = await fetchUscore('Practitioner');
        const entries = getBundleEntries(payload);
        const providers = entries.slice(0, 6).map((practitioner: any, index: number) => {
          const nameEntry = Array.isArray(practitioner?.name) ? practitioner.name[0] : null;
          const given = nameEntry?.given?.[0] || 'Clinician';
          const family = nameEntry?.family || `#${index + 1}`;
          return {
            id: practitioner?.id || `practitioner-${index + 1}`,
            name: `Dr. ${given} ${family}`.replace(/\s+/g, ' ').trim(),
            specialty: 'General Practice',
            location: defaultLocation,
          };
        });
        setLiveProviders(providers.length > 0 ? providers : demoProviders);
        setProvidersSource(providers.length > 0 ? 'live' : 'demo');
      } catch (error) {
        setProvidersError('Unable to load providers from US Core.');
        setProvidersSource('demo');
      } finally {
        setProvidersLoading(false);
      }
    };

    const loadPatients = async () => {
      setPatientsLoading(true);
      setPatientsError('');
      try {
        const payload = await fetchUscore('Patient?name=Mccall,Sarai&birthdate=1959-08-14');
        const entries = getBundleEntries(payload);
        const patients = entries.slice(0, 3).map((patient: any, index: number) => {
          const nameEntry = Array.isArray(patient?.name) ? patient.name[0] : null;
          const given = nameEntry?.given?.[0] || 'Patient';
          const family = nameEntry?.family || `#${index + 1}`;
          const telecom = Array.isArray(patient?.telecom) ? patient.telecom : [];
          const phone = telecom.find((item: any) => item.system === 'phone')?.value || '(555) 000-0000';
          const email = telecom.find((item: any) => item.system === 'email')?.value || 'patient@example.com';
          return {
            id: patient?.id || `patient-${index + 1}`,
            name: `${given} ${family}`.replace(/\s+/g, ' ').trim(),
            dob: patient?.birthDate || '1959-08-14',
            phone,
            email,
          };
        });
        setLivePatients(patients.length > 0 ? patients : demoPatients);
        setPatientsSource(patients.length > 0 ? 'live' : 'demo');
      } catch (error) {
        setPatientsError('Unable to load patients from US Core.');
        setPatientsSource('demo');
      } finally {
        setPatientsLoading(false);
      }
    };

    const loadAll = async () => {
      const defaultLocation = await loadLocations();
      await loadProviders(defaultLocation);
      await loadPatients();
    };
    loadAll();
  }, [accessToken, fhirBase]);

  const buildUscoreUrl = (rawUrl: string) => {
    if (!fhirBase) return rawUrl;
    try {
      const raw = new URL(rawUrl);
      const base = new URL(fhirBase);
      const basePath = base.pathname.replace(/\/+$/, '');
      let path = raw.pathname;
      if (path.startsWith(basePath)) {
        path = path.slice(basePath.length);
      }
      return `${fhirBase.replace(/\/+$/, '')}${path}${raw.search}`;
    } catch (error) {
      return rawUrl;
    }
  };

  const runUscoreRequest = async (entry: UscoreRequest) => {
    if (!accessToken) {
      setUscoreRunError('Access token required to run US Core requests.');
      return;
    }
    setUscoreRunError('');
    const url = buildUscoreUrl(entry.rawUrl);
    setUscoreResults((prev) => ({
      ...prev,
      [entry.id]: {
        status: 'loading',
      },
    }));
    const start = performance.now();
    try {
      const response = await fetch(url, {
        method: entry.method || 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/fhir+json',
        },
      });
      const text = await response.text();
      const durationMs = Math.round(performance.now() - start);
      if (!response.ok) {
        setUscoreResults((prev) => ({
          ...prev,
          [entry.id]: {
            status: 'error',
            code: response.status,
            durationMs,
            error: text.slice(0, 300),
          },
        }));
        return;
      }
      setUscoreResults((prev) => ({
        ...prev,
        [entry.id]: {
          status: 'success',
          code: response.status,
          durationMs,
          snippet: text.slice(0, 500),
        },
      }));
    } catch (error) {
      setUscoreResults((prev) => ({
        ...prev,
        [entry.id]: {
          status: 'error',
          error: 'Request failed (network or CORS).',
        },
      }));
    }
  };

  const runUscoreBatch = async (entries: UscoreRequest[]) => {
    if (!accessToken) {
      setUscoreRunError('Access token required to run US Core requests.');
      return;
    }
    setUscoreRunError('');
    setUscoreRunning(true);
    for (const entry of entries) {
      await runUscoreRequest(entry);
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    setUscoreRunning(false);
  };

  const handlePatientSearch = () => {
    const match = resolvedPatients.find(
      (patient) =>
        patient.name.toLowerCase().includes(searchName.toLowerCase()) &&
        patient.dob === searchDob
    );
    setSelectedPatient(match || null);
    setActiveStep(2);
  };

  const handleFhirCall = async (path: string) => {
    const trimmedBase = fhirBase.replace(/\/+$/, '');
    const url = `${trimmedBase}/${path}`;
    setFhirStatus('loading');
    setFhirEndpoint(path);
    setFhirError('');
    setFhirResponse('');

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/fhir+json',
        },
      });
      const text = await response.text();
      if (!response.ok) {
        setFhirStatus('error');
        setFhirError(`HTTP ${response.status}: ${text.slice(0, 280)}`);
        return;
      }
      setFhirStatus('success');
      setFhirResponse(text.slice(0, 2200));
    } catch (error) {
      setFhirStatus('error');
      setFhirError('Request failed (network or CORS). Use Postman token in a server-side call if needed.');
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div>
          <p className={styles.kicker}>Meditech Greenfield Scheduling</p>
          <h1>Patient scheduling E2E workflow</h1>
          <p className={styles.subtitle}>
            This view shows the patient scheduling journey with real FHIR sandbox calls where
            available and mocked flows where access is pending. It maps directly to Greenfield
            scheduling resources and the US Core endpoints in the provided Postman collection.
          </p>
          <div className={styles.dataSourceRow}>
            <span className={styles.dataSourceLabel}>
              Patient data: {patientsSource === 'live' ? 'US Core' : 'Sample'}
            </span>
            <span className={styles.dataSourceLabel}>
              Locations: {locationsSource === 'live' ? 'US Core' : 'Sample'}
            </span>
            <span className={styles.dataSourceLabel}>
              Providers: {providersSource === 'live' ? 'US Core' : 'Sample'}
            </span>
          </div>
          {(patientsError || locationsError || providersError) && (
            <p className={styles.helperText}>{patientsError || locationsError || providersError}</p>
          )}
        </div>
        <div className={styles.heroCard}>
          <h3>Scheduling overview</h3>
          <ul>
            <li>Search patient, confirm demographics and contact.</li>
            <li>Select appointment type, location, and provider.</li>
            <li>Pick an available slot and confirm booking.</li>
            <li>Show API coverage map and live FHIR calls.</li>
          </ul>
        </div>
      </section>

      <section className={styles.grid}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Scheduling workflow</h2>
            <button type="button" onClick={resetBooking} className={styles.secondaryButton}>
              Reset
            </button>
          </div>

          <div className={styles.stepper}>
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`${styles.step} ${activeStep === step ? styles.stepActive : ''} ${
                  activeStep > step ? styles.stepDone : ''
                }`}
              >
                <span className={styles.stepIndex}>{step}</span>
                <span className={styles.stepLabel}>
                  {step === 1 && 'Patient'}
                  {step === 2 && 'Details'}
                  {step === 3 && 'Slot'}
                  {step === 4 && 'Confirm'}
                </span>
              </div>
            ))}
          </div>

          {activeStep === 1 && (
            <div className={styles.stepBody}>
              <div className={styles.formRow}>
                <label>
                  Patient name
                  <input value={searchName} onChange={(e) => setSearchName(e.target.value)} />
                </label>
                <label>
                  Date of birth
                  <input value={searchDob} onChange={(e) => setSearchDob(e.target.value)} />
                </label>
              </div>
              <div className={styles.actionsRow}>
                <button type="button" onClick={handlePatientSearch} className={styles.primaryButton}>
                  Search patient
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const fallback = resolvedPatients[0];
                    if (!fallback) return;
                    setSearchName(fallback.name);
                    setSearchDob(fallback.dob);
                    setSelectedPatient(fallback);
                    setActiveStep(2);
                  }}
                  className={styles.secondaryButton}
                >
                  Use sample patient
                </button>
              </div>
              <div className={styles.helperText}>
                Matches against sandbox Patient data from the provided Postman collection.
              </div>
              {selectedPatient ? (
                <div className={styles.summaryPanel}>
                  <h4>Patient found</h4>
                  <p>{selectedPatient.name}</p>
                  <p>DOB: {selectedPatient.dob}</p>
                  <p>Phone: {selectedPatient.phone}</p>
                  <p>Email: {selectedPatient.email}</p>
                </div>
              ) : (
                <div className={styles.summaryPanelMuted}>No patient selected yet.</div>
              )}
            </div>
          )}

          {activeStep === 2 && (
            <div className={styles.stepBody}>
              <div className={styles.formRow}>
                <label>
                  Appointment type
                  <select value={appointmentType} onChange={(e) => setAppointmentType(e.target.value as 'In-Person' | 'Virtual')}>
                    <option value="In-Person">In-Person</option>
                    <option value="Virtual">Virtual</option>
                  </select>
                </label>
                <label>
                  Location
                  <select value={location} onChange={(e) => setLocation(e.target.value)}>
                    {resolvedLocations.map((loc) => (
                      <option key={loc} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={styles.formRow}>
                <label>
                  Provider
                  <select
                    value={selectedProvider?.id || ''}
                    onChange={(e) =>
                      setSelectedProvider(
                        availableProviders.find((provider) => provider.id === e.target.value) || null
                      )
                    }
                  >
                    <option value="">Any available provider</option>
                    {availableProviders.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} · {provider.specialty}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Visit reason
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Annual check-up" />
                </label>
              </div>
              {(providersLoading || locationsLoading || patientsLoading) && (
                <p className={styles.helperText}>Loading live US Core data...</p>
              )}
              <div className={styles.actionsRow}>
                <button type="button" onClick={() => setActiveStep(3)} className={styles.primaryButton}>
                  Continue to slot selection
                </button>
                <button type="button" onClick={() => setActiveStep(1)} className={styles.secondaryButton}>
                  Back
                </button>
              </div>
            </div>
          )}

          {activeStep === 3 && (
            <div className={styles.stepBody}>
              <div className={styles.slotGrid}>
                {availableSlots.map((slot) => {
                  const provider = demoProviders.find((item) => item.id === slot.providerId);
                  const isSelected = selectedSlot?.id === slot.id;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      className={`${styles.slotCard} ${isSelected ? styles.slotCardActive : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                    >
                      <div className={styles.slotTime}>{slot.date}</div>
                      <div className={styles.slotTime}>{slot.time}</div>
                      <div className={styles.slotMeta}>{provider?.name}</div>
                      <div className={styles.slotMeta}>{provider?.specialty}</div>
                    </button>
                  );
                })}
              </div>
              <div className={styles.actionsRow}>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => setActiveStep(4)}
                  disabled={!selectedSlot}
                >
                  Review and confirm
                </button>
                <button type="button" onClick={() => setActiveStep(2)} className={styles.secondaryButton}>
                  Back
                </button>
              </div>
            </div>
          )}

          {activeStep === 4 && (
            <div className={styles.stepBody}>
              <div className={styles.summaryPanel}>
                <h4>Appointment summary</h4>
                <p>Patient: {selectedPatient?.name || 'Not selected'}</p>
                <p>Type: {appointmentType}</p>
                <p>Location: {location}</p>
                <p>
                  Provider:{' '}
                  {selectedProvider?.name ||
                    demoProviders.find((provider) => provider.id === selectedSlot?.providerId)?.name ||
                    'Any'}
                </p>
                <p>Slot: {selectedSlot ? `${selectedSlot.date} · ${selectedSlot.time}` : 'Not selected'}</p>
                <p>Reason: {notes || 'Routine visit'}</p>
              </div>
              <div className={styles.actionsRow}>
                <button type="button" className={styles.primaryButton}>
                  Book appointment
                </button>
                <button type="button" onClick={() => setActiveStep(3)} className={styles.secondaryButton}>
                  Back
                </button>
              </div>
              <p className={styles.helperText}>
                Booking maps to FHIR Appointment creation once scheduling access is enabled.
              </p>
            </div>
          )}
        </div>

        <div className={styles.stack}>
          <div className={styles.card}>
            <h2>API coverage map</h2>
            <p className={styles.subtitle}>
              Highlights which Greenfield APIs are live in sandbox today and which are queued for
              production access.
            </p>
            <div className={styles.coverageList}>
              {apiCoverage.map((item) => (
                <div key={item.area} className={styles.coverageItem}>
                  <div>
                    <h4>{item.area}</h4>
                    <p>{item.endpoints.join(', ')}</p>
                  </div>
                  <div className={styles.statusGroup}>
                    <span className={`${styles.badge} ${styles[`badge${item.status}`]}`}>
                      {item.status === 'live' && 'Live'}
                      {item.status === 'mock' && 'Mocked'}
                      {item.status === 'restricted' && 'Pending'}
                    </span>
                    <span className={styles.coverageNote}>{item.note}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <h2>Scheduling API workflow map</h2>
            <p className={styles.subtitle}>
              Step-by-step flow showing which Greenfield APIs are used at each stage of patient
              scheduling (based on the scraped Meditech specs).
            </p>
            <div className={styles.workflowList}>
              {workflowSteps.map((item) => (
                <div key={item.step} className={styles.workflowItem}>
                  <div className={styles.workflowHeader}>
                    <div>
                      <h4>{item.step}</h4>
                      <p>{item.purpose}</p>
                    </div>
                    <span className={`${styles.badge} ${styles[`badge${item.status}`]}`}>
                      {item.status === 'live' && 'Live'}
                      {item.status === 'mock' && 'Mocked'}
                      {item.status === 'restricted' && 'Pending'}
                    </span>
                  </div>
                  <div className={styles.endpointList}>
                    {item.endpoints.map((endpoint) => (
                      <span key={endpoint} className={styles.endpointChip}>
                        {endpoint}
                      </span>
                    ))}
                  </div>
                  <p className={styles.coverageNote}>{item.note}</p>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <h2>FAQ for Meditech review</h2>
            <p className={styles.subtitle}>
              Quick answers to common questions about scheduling APIs, scope, and connectivity.
            </p>
            <div className={styles.faqList}>
              {faqItems.map((item) => (
                <details key={item.question} className={styles.faqItem}>
                  <summary className={styles.faqSummary}>{item.question}</summary>
                  <p className={styles.faqAnswer}>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <h2>Integration steps for Expanse scheduling</h2>
            <p className={styles.subtitle}>
              Step-by-step sequence to connect to a client Expanse tenant and enable Annual Wellness
              scheduling through Greenfield APIs.
            </p>
            <div className={styles.integrationList}>
              {integrationSteps.map((item) => (
                <div key={item.step} className={styles.integrationItem}>
                  <div className={styles.integrationHeader}>
                    <div>
                      <h4>{item.step}</h4>
                      <p>{item.owner}</p>
                    </div>
                  </div>
                  <ul>
                    {item.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <h2>Live FHIR sandbox call</h2>
            <p className={styles.subtitle}>
              Paste a Postman token (authorization_code flow) to call US Core endpoints directly.
            </p>
            <div className={styles.formRow}>
              <label>
                FHIR base URL
                <input value={fhirBase} onChange={(e) => setFhirBase(e.target.value)} />
              </label>
            </div>
            <div className={styles.formRow}>
              <label>
                Access token
                <input
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="Bearer token from Postman"
                />
              </label>
            </div>
            <div className={styles.formRow}>
              <label>
                Patient ID
                <input value={fhirPatientId} onChange={(e) => setFhirPatientId(e.target.value)} />
              </label>
            </div>
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => handleFhirCall('metadata')}
                disabled={!accessToken}
              >
                Test metadata
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => handleFhirCall(`Patient/${fhirPatientId}`)}
                disabled={!accessToken}
              >
                Load patient
              </button>
            </div>
            <div className={styles.responsePanel}>
              <div className={styles.responseHeader}>
                <span>Endpoint: {fhirEndpoint}</span>
                <span className={styles.statusPill}>
                  {fhirStatus === 'idle' && 'Idle'}
                  {fhirStatus === 'loading' && 'Loading'}
                  {fhirStatus === 'success' && 'Success'}
                  {fhirStatus === 'error' && 'Error'}
                </span>
              </div>
              {fhirError && <p className={styles.errorText}>{fhirError}</p>}
              {fhirResponse ? (
                <pre className={styles.responseBody}>{fhirResponse}</pre>
              ) : (
                <p className={styles.helperText}>Response payload will appear here.</p>
              )}
            </div>
          </div>

          <div className={styles.card}>
            <h2>US Core API catalog (Postman collection)</h2>
            <p className={styles.subtitle}>
              Runs every US Core endpoint from the provided Postman collection. Requires a token
              and may be blocked by CORS in the browser.
            </p>
            <div className={styles.formRow}>
              <label>
                Filter by name or path
                <input value={uscoreFilter} onChange={(e) => setUscoreFilter(e.target.value)} />
              </label>
              <label>
                Total endpoints
                <input value={`${filteredUscoreRequests.length}`} readOnly />
              </label>
            </div>
            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => runUscoreBatch(uscoreRequests)}
                disabled={uscoreRunning || uscoreRequests.length === 0}
              >
                Run all endpoints
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => runUscoreBatch(filteredUscoreRequests)}
                disabled={uscoreRunning || filteredUscoreRequests.length === 0}
              >
                Run filtered
              </button>
            </div>
            {uscoreRunError && <p className={styles.errorText}>{uscoreRunError}</p>}
            <div className={styles.uscoreList}>
              {filteredUscoreRequests.slice(0, uscoreVisibleCount).map((entry) => {
                const result = uscoreResults[entry.id];
                const status = result?.status || 'idle';
                const displayUrl = buildUscoreUrl(entry.rawUrl);
                return (
                  <div key={entry.id} className={styles.uscoreItem}>
                    <div className={styles.uscoreHeader}>
                      <div>
                        <h4>{entry.name}</h4>
                        <p className={styles.uscoreMeta}>
                          {entry.method} {displayUrl}
                        </p>
                      </div>
                      <div className={styles.uscoreActions}>
                        <span className={`${styles.uscoreStatus} ${styles[`status${status}`]}`}>
                          {status}
                        </span>
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          onClick={() => runUscoreRequest(entry)}
                        >
                          Run
                        </button>
                      </div>
                    </div>
                    {result?.snippet && <pre className={styles.uscoreSnippet}>{result.snippet}</pre>}
                    {result?.error && <p className={styles.errorText}>{result.error}</p>}
                    {result?.code && (
                      <p className={styles.helperText}>
                        HTTP {result.code} · {result.durationMs ?? 0} ms
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            {filteredUscoreRequests.length > uscoreVisibleCount && (
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setUscoreVisibleCount((prev) => prev + 12)}
              >
                Show more
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MeditechSchedulingDemo;
